import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import usePlaceSearch from '../hooks/usePlaceSearch';
import { utilService } from '../../../services/api';

const Wrapper = styled.div`position:relative;`;
const Label = styled.label`
  display:block;
  font-size:${p => p.theme.typography.sizes.sm};
  font-weight:${p => p.theme.typography.weights.semibold};
  color:${p => p.theme.colors.text.primary};
  margin-bottom:6px;
`;
const Input = styled.input`
  width:100%;padding:10px 12px;
  border:1px solid ${p => p.theme.colors.border};
  border-radius:${p => p.theme.radius.md}px;
  font-size:${p => p.theme.typography.sizes.md};
  box-sizing:border-box;
  &:focus{border-color:${p => p.theme.colors.primary};outline:none;box-shadow:0 0 0 2px rgba(108,99,255,0.15);}
  &:disabled{opacity:0.5;cursor:not-allowed;}
`;
const SuggestionList = styled.ul`
  position:absolute;top:100%;left:0;right:0;z-index:10;
  background:${p => p.theme.colors.surface};
  border:1px solid ${p => p.theme.colors.border};
  border-radius:0 0 ${p => p.theme.radius.md}px ${p => p.theme.radius.md}px;
  list-style:none;margin:0;padding:0;max-height:260px;overflow-y:auto;
  box-shadow:${p => p.theme.shadow.elevated};
`;
const SuggestionItem = styled.li`
  padding:12px;cursor:pointer;
  font-size:${p => p.theme.typography.sizes.sm};
  border-bottom:1px solid ${p => p.theme.colors.border};
  min-height:44px;display:flex;align-items:center;
  background:${p => p.$active ? p.theme.colors.background : 'transparent'};
  color:${p => p.theme.colors.text.primary};
  &:hover{background:${p => p.theme.colors.background};}
  &:last-child{border-bottom:none;}
`;
const CoordDisplay = styled.div`
  font-size:${p => p.theme.typography.sizes.xs};
  color:${p => p.theme.colors.text.muted};
  margin-top:4px;
  font-family:${p => p.theme.typography.monoFamily};
`;

export default function PlacePicker({
  value, lat, lon, timezone,
  onChange, label = 'Place of Birth', showCoords = true, disabled = false,
}) {
  const search = usePlaceSearch();
  const [activeIdx, setActiveIdx] = useState(-1);
  const [resolving, setResolving] = useState(false);
  const [tz, setTz] = useState(timezone || 'Asia/Kolkata');

  useEffect(() => {
    if (value && value !== search.place) search.setPlace(value);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resolveAndNotify = useCallback(async (s) => {
    search.select(s);
    setResolving(true);
    let resolvedTz = 'Asia/Kolkata';
    try {
      const res = await utilService.getTimezone(s.lat, s.lon);
      resolvedTz = res.data.timezone;
    } catch {
      const offset = Math.round(s.lon / 15);
      const map = { '5':'Asia/Kolkata', '-5':'America/New_York', '0':'Europe/London', '8':'Asia/Singapore', '9':'Asia/Tokyo' };
      resolvedTz = map[String(offset)] || 'UTC';
    }
    setTz(resolvedTz);
    setResolving(false);
    if (onChange) onChange({ place: s.label, lat: s.lat, lon: s.lon, timezone: resolvedTz });
  }, [search, onChange]);

  const handleKeyDown = (e) => {
    const len = search.suggestions.length;
    if (!len) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => (i + 1) % len); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => (i - 1 + len) % len); }
    else if (e.key === 'Enter' && activeIdx >= 0) { e.preventDefault(); resolveAndNotify(search.suggestions[activeIdx]); setActiveIdx(-1); }
    else if (e.key === 'Escape') { search.select({ label: search.place, lat: 0, lon: 0 }); setActiveIdx(-1); }
  };

  const displayLat = search.lat ?? lat;
  const displayLon = search.lon ?? lon;
  const displayTz = resolving ? 'detecting...' : tz;

  return (
    <Wrapper ref={search.wrapperRef}>
      <Label>{label}</Label>
      <Input
        value={search.place}
        onChange={(e) => { search.handleChange(e); setActiveIdx(-1); }}
        onKeyDown={handleKeyDown}
        placeholder="Type a place name..."
        disabled={disabled}
        autoComplete="off"
      />
      {search.loading && <CoordDisplay>Searching...</CoordDisplay>}
      {search.suggestions.length > 0 && (
        <SuggestionList>
          {search.suggestions.map((s, i) => (
            <SuggestionItem key={i} $active={i === activeIdx} onClick={() => { resolveAndNotify(s); setActiveIdx(-1); }}>
              {s.label}
            </SuggestionItem>
          ))}
        </SuggestionList>
      )}
      {showCoords && displayLat != null && displayLat !== 0 && (
        <CoordDisplay>
          {Math.abs(displayLat).toFixed(2)}&deg;{displayLat >= 0 ? 'N' : 'S'},{' '}
          {Math.abs(displayLon).toFixed(2)}&deg;{displayLon >= 0 ? 'E' : 'W'}{' '}
          &mdash; {displayTz}
        </CoordDisplay>
      )}
    </Wrapper>
  );
}
